(ns dre.components.table
  (:require
   ["antd" :refer [Table]]
   ["@dnd-kit/core" :refer [DndContext PointerSensor useSensor useSensors]]
   ["@dnd-kit/modifiers" :refer [restrictToVerticalAxis]]
   ["@dnd-kit/sortable" :refer [SortableContext useSortable verticalListSortingStrategy]]
   ["@dnd-kit/utilities" :refer [CSS]]
   [clojure.string :as str]
   [dre.utils :refer [stringify-keys-with-namespaces]]
   [goog.object]
   [reagent.core :as reagent]))

(defn- wrap-render
  "Wraps render function (if exists) to return an element instead of a reagent component."
  [{:keys [render] :as column}]
  (if (nil? render)
    column
    (assoc column :render
           (fn [_ record _]
             (let [record (js->clj record :keywordize-keys true)]
               (reagent/as-element
                [render record]))))))

(defn- default-sorter
  [dataIndex x y]
  (let [extract (fn [record]
                  (-> record
                      (js->clj :keywordize-keys true)
                      dataIndex))
        x (extract x)
        x (if (string? x) (str/lower-case x) x)
        y (extract y)
        y (if (string? y) (str/lower-case y) y)]
    (compare x y)))

(defn- wrap-sorter
  [{:keys [sorter dataIndex] :as column}]
  (case sorter
    nil column
    :default (assoc column :sorter (partial default-sorter dataIndex))
    (assoc column :sorter
           (fn [x y]
             (sorter
              (js->clj x :keywordize-keys true)
              (js->clj y :keywordize-keys true))))))

(defn- table-
  [{:keys [columns dataSource rowKey components pagination rowSelection loading? expandable size title]
    :or {pagination {:defaultPageSize 1000
                     :hideOnSinglePage true}
         loading? false
         expandable nil
         title nil}}]
  [:> Table
   {:columns columns
    :dataSource (clj->js (stringify-keys-with-namespaces  dataSource))
    :rowKey rowKey
    :pagination pagination
    :components components
    :rowSelection rowSelection
    :loading loading?
    :size size
    :title title
    :expandable expandable}])

(defn- ->sensors
  []
  (useSensors
   (useSensor PointerSensor (clj->js {:activationConstraint {:distance 1}}))))


(defn- drag-sortable-table
  [{:keys [dataSource on-drag-end] :as opts}]
  [:> DndContext
   {:sensors (->sensors)
    :modifiers [restrictToVerticalAxis]
    :onDragEnd on-drag-end}
   [:> SortableContext
    {:items (map :idx dataSource)
     :strategy verticalListSortingStrategy}
    [table- opts]]])

(defn- use-sortable
  [id]
  (js->clj
   (useSortable (clj->js {:id id}))
   :keywordize-keys true))

(defn- ->transform-str
  [transform]
  (when transform
    (.toString
     (.-Transform CSS)
     (clj->js
      (merge transform
             {:scaleY 1})))))

(defn- ->drag-sortable-row-style
  [{:keys [style] :as _props}
   {:keys [transform transition isDragging] :as _sortable-ctx}]
  (merge style
         {:transform (->transform-str transform)
          :transition transition
          :cursor :move}
         (when isDragging
           {:position :relative
            :zIndex 9999})))

(defn- drag-sortable-row
  [props]
  (let [children (.-children props)
        props (js->clj props :keywordize-keys true)
        {:keys [attributes
                listeners
                setNodeRef] :as sortable-ctx} (use-sortable (:data-row-key props))
        style (->drag-sortable-row-style props sortable-ctx)]
    (reagent/as-element
     [:tr (merge props
                 attributes
                 listeners
                 {:ref setNodeRef
                  :style style})
      children])))

(defn- assoc-idx
  [dataSource]
  (map #(assoc %1 :idx %2)
       dataSource
       (map inc (range)))) ; ids should start from 1 instead of 0. b/c 0 breaks dragging

(defn table
  [{:keys [drag-sortable?] :as opts}]
  (let [opts (-> opts
                 (update :columns #(map (comp wrap-render wrap-sorter) %))
                 (update :dataSource assoc-idx)
                 (update :rowKey #(or % :idx)))]
    (if drag-sortable?
      [:f> drag-sortable-table (assoc opts :components {:body {:row drag-sortable-row}})]
      [table- opts])))
